import { HttpClient } from "@angular/common/http";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { DataService } from "src/app/shared/Services/data.service";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as moment from "moment";

@Component({
  selector: "app-auditlogs",
  templateUrl: "./auditlogs.component.html",
  styleUrls: ["./auditlogs.component.scss"],
})
export class AuditlogsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  subscriptions: Subscription[] = [];

  /** UI And State Variables */
  breadCrumbItems: any[] = [];
  customerList: any[] = [];
  selectedCustomer: any;

  /** Dates */
  maxDate!: string;
  fromDate!: string;
  toDate!: string;

  /** Table Data */
  logColumns: string[] = [];
  logRows: any[] = [];

  filteredRows: any[] = [];
  columnOptions: any = {};
  filters: any = {};

  /** Pagination */
  currentPage: number = 1;
  itemsPerPage: number = 10;

  /** Columns without filters */
  noFilterColumns: string[] = [
    "date",
    "time",
    "duration",
    "createdat",
    "updatedat",
    "timestamp",
  ];

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private dataService: DataService
  ) { }

  ngOnInit(): void {
    this.initializeDates();
    this.initializeBreadcrumb();
    this.loadCustomerList();
  }

  /** Initialize Today's Date */
  private initializeDates(): void {
    this.maxDate = moment().format("YYYY-MM-DD");
    this.fromDate = this.maxDate;
    this.toDate = this.maxDate;
  }

  private initializeBreadcrumb(): void {
    this.breadCrumbItems = [
      { label: "Audit Logs" },
      { label: "Dashboard", active: true },
    ];
  }

  /** Load Customer List */
  loadCustomerList(): void {
    const sub = this.dataService
      .get("api/user")
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        const filtered = res.filter((x: any) => x.name !== "SUPERADMIN");

        this.customerList = filtered
          .filter(
            (item: any) =>
              item?.status?.deviceDetails?.length > 0
          )
          .map((item: any) => ({
            custName: item.status.customerName,
            custId: item.status.custId,
            deviceList: item.status.deviceDetails,
            timeZone: item.timeZone,
          }));

        this.selectedCustomer = this.customerList[0];
        if (this.selectedCustomer) {
          this.getLogs();
        }
      });

    this.subscriptions.push(sub);
  }

  /** On Customer Dropdown Change */
  onCustomerChange(cust: any): void {
    this.resetTable();
    this.selectedCustomer = cust;
    this.getLogs();
  }

  /** Reset Table Data */
  private resetTable(): void {
    this.logColumns = [];
    this.logRows = [];
    this.filteredRows = [];
    this.filters = {};
    this.columnOptions = {};
  }

  /** Fetch Logs API */
  getLogs(): void {
    if (!this.selectedCustomer) return;

    if (this.fromDate > this.toDate) {
      this.toastr.info("Selected Date is Invalid");
      return;
    }

    const payload = {
      custID: this.selectedCustomer.custId,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };

    const sub = this.dataService
      .reportApi("auditlog", payload)
      .subscribe(
        (res: any) => this.processLogData(res),
        () => this.toastr.error("Server Unreachable")
      );
    this.subscriptions.push(sub);
  }

  /** Process Response */
  private processLogData(res: any): void {
    this.logColumns = res.columns || [];
    this.logRows = res.rows || [];
    if (!this.logRows.length) {
      this.filteredRows = [];
      return;
    }
    this.filteredRows = [...this.logRows];

    /** Build Filter Options */
    this.logColumns.forEach((col) => {
      const values = [...new Set(this.logRows.map((row) => row[col]))];
      this.columnOptions[col] = values;
      this.filters[col] = "";
    });
  }

  /** Apply Filter on each column */
  setFilter(column: string, value: any) {
    this.filters[column] = value;
    this.filteredRows = this.logRows.filter((row) =>
      Object.keys(this.filters).every(
        (col) => !this.filters[col] || row[col] === this.filters[col]
      )
    );
    this.currentPage = 1;
  }

  onPageChange(page: number) {
    this.currentPage = page;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
